import React, { useEffect, useState } from "react";
import {
  Box, Button, Flex, Table, Tbody, Td, Th, Thead, Tr, Text, useToast, Spinner,
  Heading, Input,
} from "@chakra-ui/react";
import { getSuspensions, liftSuspension, suspendProvider } from "../../utils/apis/agentApi";

/** Suspended providers: taken out of matching until a human clears it.
 *
 *  This page IS the appeal path. A suspended provider cannot lift it themselves
 *  (their RESUME is refused on purpose, so a quality suspension is not undone by
 *  typing one word), which means without somewhere for a person to see the list
 *  and release them, a suspension would be permanent by accident. Nothing here
 *  is automatic: the agent's auto-suspend is off by default and has to be turned
 *  on deliberately. */
const Suspensions = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [newId, setNewId] = useState("");
  const [newReason, setNewReason] = useState("");
  const toast = useToast();

  const load = async () => {
    setLoading(true);
    const res = await getSuspensions();
    if (res.ok && Array.isArray(res.data)) setRows(res.data);
    else
      toast({
        title: res.status === 502 ? "Agent unreachable" : "Failed to load",
        status: "error", duration: 3000, isClosable: true,
      });
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const lift = async (providerId: string) => {
    const res = await liftSuspension(providerId);
    toast({
      title: res.ok ? "Suspension lifted" : "Could not lift it",
      description: res.ok
        ? "They will start receiving job offers again."
        : "Nothing changed. Try again, or check the agent is reachable.",
      status: res.ok ? "success" : "error", duration: 3000, isClosable: true,
    });
    if (res.ok) load();
  };

  const add = async () => {
    if (!newId.trim()) return;
    const res = await suspendProvider(newId.trim(), newReason.trim() || undefined);
    toast({
      title: res.ok ? "Provider suspended" : "Could not suspend",
      status: res.ok ? "success" : "error", duration: 3000, isClosable: true,
    });
    if (res.ok) { setNewId(""); setNewReason(""); load(); }
  };

  return (
    <Box p={5}>
      <Flex justifyContent="space-between" alignItems="center" mb={1}>
        <Heading size="md">Suspended providers</Heading>
        <Button size="sm" onClick={load} isDisabled={loading}>Refresh</Button>
      </Flex>
      <Text fontSize="sm" color="admin.muted" mb={4}>
        Suspended providers receive no job offers and cannot lift it themselves.
        Lifting it here is the only way back, so check the reason before you do.
      </Text>

      <Flex gap={2} mb={5} flexWrap="wrap">
        <Input size="sm" maxW="260px" placeholder="Provider ID"
               value={newId} onChange={(e) => setNewId(e.target.value)} />
        <Input size="sm" maxW="360px" placeholder="Reason (shown to the reviewer)"
               value={newReason} onChange={(e) => setNewReason(e.target.value)} />
        <Button size="sm" colorScheme="red" onClick={add} isDisabled={!newId.trim()}>
          Suspend
        </Button>
      </Flex>

      {loading ? (
        <Flex justifyContent="center" py={10}><Spinner /></Flex>
      ) : rows.length === 0 ? (
        <Text color="admin.muted">
          Nobody is suspended. Every verified provider is currently eligible for
          job offers.
        </Text>
      ) : (
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Provider</Th>
              <Th>ID</Th>
              <Th>Reason</Th>
              <Th>Since</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((r) => (
              <Tr key={r.provider_id}>
                <Td>{r.provider_name || "unknown"}</Td>
                <Td fontSize="xs" color="admin.muted">{r.provider_id}</Td>
                <Td>{r.suspended_reason || "no reason recorded"}</Td>
                <Td fontSize="xs">
                  {r.suspended_at ? new Date(r.suspended_at).toLocaleString() : ""}
                </Td>
                <Td>
                  <Button size="xs" onClick={() => lift(r.provider_id)}>
                    Lift suspension
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </Box>
  );
};

export default Suspensions;
